import { useMutation, useQueryClient } from '@tanstack/react-query';
import z from 'zod';
import {
  StatusCode,
  patchApiV2CustomersCustomerIdAddress,
} from '@hungryroot/rest';
import { invalidateAccountSettings } from './useGetAccountSettings';

export const ShippingDetailsSchema = z.object({
  name: z
    .string({
      required_error: 'Name is a required field',
    })
    .trim()
    .max(100, {
      message: 'Name must be less than 100 characters',
    }),
  address1: z
    .string({ required_error: 'Street Address is a required field' })
    .trim()
    .max(100, {
      message: 'Street Address must be less than 100 characters',
    }),
  address2: z
    .string()
    .trim()
    .max(100, {
      message: 'Address Line 2 must be less than 100 characters',
    })
    .optional()
    .default(''),
  city: z
    .string({ required_error: 'City is a required field' })
    .trim()
    .max(50, {
      message: 'City must be less than 50 characters',
    }),
  state: z
    .string({ required_error: 'State is a required field' })
    .trim()
    .max(2, {
      message: 'State must be less than 2 characters',
    }),
  zip_code: z
    .string({ required_error: 'Zip Code is a required field' })
    .trim()
    .max(10, {
      message: 'Zip Code must be less than 10 characters',
    }),
  note: z.string().trim().optional().default(''),
  phone: z
    .string({ required_error: 'Phone is a required field' })
    .trim()
    .max(20, {
      message: 'Phone must be less than 20 characters',
    }),
});

export type ShippingDetailsPayload = z.infer<typeof ShippingDetailsSchema>;

export function useUpdateShippingDetails() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ShippingDetailsPayload) => {
      const resp = await patchApiV2CustomersCustomerIdAddress({
        requestBody: payload,
      });

      if (resp.status !== StatusCode.SuccessOK) {
        throw new Error(JSON.stringify(resp.data, null, 2));
      }

      return resp.data;
    },
    onSuccess() {
      invalidateAccountSettings(queryClient);
    },
  });
}
